import FullBox, { type FullBoxProps } from '../full-box';
import classes from './index.module.scss';
import type { MyTableHeaderProps } from './components/my-table-header';
import MyTableHeader from './components/my-table-header';
import MyTableFooter from './components/my-table-footer';
import type { MyTablePaginationProps } from './components/my-table-pagination';
import MyTablePagination from './components/my-table-pagination';
import type { ParamsType, ProTableProps } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import type { CustomValueTypes } from '@/configs/theme/valueTypeMap';

export interface MyTableProps<
  DataSource extends Record<string, any> = Record<string, any>,
  U extends ParamsType = ParamsType,
  ValueType = 'text',
> extends ProTableProps<DataSource, U, CustomValueTypes | ValueType> {
  fullBoxProps?: Omit<FullBoxProps, 'children'>;
  headerProps?: MyTableHeaderProps;
  paginationProps?: MyTablePaginationProps;
}

export default function MyTable<
  DataSource extends Record<string, any> = Record<string, any>,
  U extends ParamsType = ParamsType,
  ValueType = 'text',
>({
  className,
  fullBoxProps = {},
  headerProps,
  paginationProps,
  rootClassName,
  size,
  ...props
}: MyTableProps<DataSource, U, ValueType>) {
  const { centerClassName, ...fullBoxProps_ } = fullBoxProps;
  const rootRef = useRef<HTMLDivElement>(null);

  const calcColumnWidth = useCallback(() => {
    const headerTableDom = rootRef.current
      ?.querySelector('.ant-table-header')
      ?.querySelector('table');
    const headerColDoms = headerTableDom
      ?.querySelector('colgroup')
      ?.querySelectorAll('col');
    const headerThDoms = headerTableDom?.querySelector('tr')?.querySelectorAll('th');

    const bodyColDoms = rootRef.current
      ?.querySelector('.ant-table-body')
      ?.querySelector('colgroup')
      ?.querySelectorAll('col');

    if (headerTableDom && headerColDoms && headerThDoms && bodyColDoms) {
      const range = new Range();
      headerTableDom.style.tableLayout = 'auto';
      for (let i = 0; i < headerColDoms.length; i++) {
        const headerColDom = headerColDoms[i];
        const headerThDom = headerThDoms[i];
        const bodyColDom = bodyColDoms[i];

        if (headerColDom && headerThDom && bodyColDom) {
          if (!bodyColDom.style.width && headerThDom.childNodes.length) {
            range.setStartBefore(headerThDom.firstChild!);
            range.setEndAfter(headerThDom.lastChild!);

            const rangeWidth = range.getBoundingClientRect().width;

            const cs = getComputedStyle(headerThDom);
            const inlinePadding =
              parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) || 0;

            const headerColContentWidth = rangeWidth + inlinePadding;
            const bodyTdWidth = bodyColDom.offsetWidth;

            if (bodyTdWidth < headerColContentWidth) {
              const widthValue = headerColContentWidth + 'px';
              bodyColDom.style.width = widthValue;
            }
          }
        }
      }
      headerTableDom.style.tableLayout = 'fixed';
    }
  }, []);

  useLayoutEffect(() => {
    calcColumnWidth();
  });

  return (
    <FullBox
      className={cls(classes.myTable, className, size)}
      ref={rootRef}
      centerClassName={cls(classes.main, centerClassName)}
      frontChildren={headerProps && <MyTableHeader {...headerProps}></MyTableHeader>}
      rearChildren={
        <MyTableFooter>
          {paginationProps && (
            <MyTablePagination {...paginationProps}></MyTablePagination>
          )}
        </MyTableFooter>
      }
      column
      {...fullBoxProps_}
    >
      <ProTable
        className={cls(classes.table, rootClassName)}
        sticky
        search={false}
        options={false}
        pagination={false}
        scroll={{ x: 'max-content' }}
        tableAlertRender={false}
        tableAlertOptionRender={false}
        size={size}
        tableLayout="auto"
        {...props}
      ></ProTable>
    </FullBox>
  );
}
